import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { mgcIconLeftRegular } from '@sb/mgc-icons/mgcIcon-left_regular.icon';
import { mgcIconMore1Regular } from '@sb/mgc-icons/mgcIcon-more_1_regular.icon';
import { mgcIconRightRegular } from '@sb/mgc-icons/mgcIcon-right_regular.icon';
import { clsx } from 'clsx';

import { ButtonComponent, IconButtonComponent } from '../button';
import { IconComponent, IconRegistry } from '../icon';
import { calculatePages } from './pagination.helper';

export interface PaginationChangeEvent {
  page: number;
}

interface Page {
  label: string;
  value: number;
  type: 'page' | 'ellipsis';
}

export const PAGINATION_DEFAULT_ITEMS_PER_PAGE = 10;
export const PAGINATION_DEFAULT_MAX_VISIBLE_PAGES = 7;

@Component({
  selector: 'sb-pagination',
  imports: [CommonModule, IconComponent, IconButtonComponent, ButtonComponent, TranslatePipe],
  templateUrl: './pagination.component.html',
})
export class PaginationComponent implements OnChanges {
  @Input()
  totalItems!: number;

  @Input()
  itemsPerPage = PAGINATION_DEFAULT_ITEMS_PER_PAGE;

  @Input()
  page = 1;

  @Input()
  maxVisiblePages = PAGINATION_DEFAULT_MAX_VISIBLE_PAGES;

  @Input()
  showCount = false;

  currentPage = signal(1);
  private _totalItems = signal(0);
  private _itemsPerPage = signal(PAGINATION_DEFAULT_ITEMS_PER_PAGE);

  pages: Page[] = [];

  // Computed values
  totalPages = computed(() => {
    return Math.ceil(this._totalItems() / this._itemsPerPage());
  });

  fromValue = computed(() => {
    return this._itemsPerPage() * this.currentPage() - this._itemsPerPage() + 1;
  });

  toValue = computed(() => {
    return Math.min(this._itemsPerPage() * this.currentPage(), this._totalItems());
  });

  canGoToPreviousPage = computed(() => {
    return this.currentPage() > 1;
  });

  canGoToNextPage = computed(() => {
    return this.currentPage() < this.totalPages();
  });

  @Output()
  pageChange = new EventEmitter<PaginationChangeEvent>();

  @HostBinding('class.hidden')
  get hidden(): boolean {
    return this.totalPages() <= 1;
  }

  @HostBinding('attr.role')
  role = 'navigation';

  @HostBinding('attr.aria-label')
  ariaLabel = 'Pagination';

  @HostBinding('class')
  hostClasses = clsx(['block']);

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconLeftRegular, mgcIconRightRegular, mgcIconMore1Regular]);
  }

  ngOnChanges(): void {
    this._totalItems.set(this.totalItems || 0);
    this._itemsPerPage.set(this.itemsPerPage || PAGINATION_DEFAULT_ITEMS_PER_PAGE);
    const newCurrentPage = Math.min(Math.max(this.page, 1), this.totalPages());
    this.currentPage.set(newCurrentPage);
    this.setVisiblePages();
  }

  goToPage(page: number): void {
    if (!this.canGoToPage(page) || page === this.currentPage()) {
      return;
    }
    this.currentPage.set(page);
    this.setVisiblePages();
    this.pageChange.emit({ page });
  }

  trackByPage(_: number, page: Page): number {
    return page.value;
  }

  private setVisiblePages(): void {
    this.pages = calculatePages({
      currentPage: this.currentPage(),
      totalPages: this.totalPages(),
      maxSize: this.maxVisiblePages,
    }).map((value, index) => ({
      label: `${value}`,
      value: value === '...' ? -index : value,
      type: value === '...' ? 'ellipsis' : 'page',
    }));
  }

  private canGoToPage(page: number): boolean {
    return page >= 1 && page <= this.totalPages();
  }
}
